import Flickity from 'flickity'
import { parseJSON } from '@/components/helpers/utils'
const defaultOptions = {
  contain: true
}
const DEFAULT_AUTOPLAY_SPEED_MS = 3000
const CAROUSEL_FPS = 60
export class Carousel extends HTMLElement {
  connectedCallback() {
    this.initializeCarousel()
    this.setupMutationObserver()
  }

  initializeCarousel() {
    const computedHeight = this.getBoundingClientRect().height

    if (computedHeight > 0) {
      this.style.minHeight = `${computedHeight}px`
    }

    const options = this.carouselSettings
    options.cellSelector = '&:not(style)'
    const isTicker = !!options.ticker
    const autoplaySpeedMs = parseFloat(options.autoplaySpeed) || DEFAULT_AUTOPLAY_SPEED_MS
    delete options.ticker
    delete options.autoplaySpeed

    const mergedOptions = { ...defaultOptions, ...options }
    if (mergedOptions.wrapAround === true) {
      delete mergedOptions.contain
    }

    if (isTicker) {
      mergedOptions.freeScroll = true
      mergedOptions.wrapAround = true
      delete mergedOptions.contain
      delete mergedOptions.autoPlay
      mergedOptions.prevNextButtons = mergedOptions.prevNextButtons ?? false
      mergedOptions.pageDots = mergedOptions.pageDots ?? false
      mergedOptions.draggable = mergedOptions.draggable ?? true
      mergedOptions.friction = mergedOptions.friction ?? 0.028
      mergedOptions.selectedAttraction = mergedOptions.selectedAttraction ?? 0.015

      this.fillTickerContent()
      this.eagerLoadAllImages()
    } else if (mergedOptions.autoPlay && mergedOptions.freeScroll === true) {
      mergedOptions.freeScroll = false
    }

    this.flickity = new Flickity(this, mergedOptions)

    if (isTicker) {
      const slideWidth = this.flickity.slideableWidth / this.flickity.slides.length
      const framesPerSlide = (autoplaySpeedMs / 1000) * CAROUSEL_FPS
      const tickerSpeed = slideWidth / framesPerSlide

      this.setupTicker(tickerSpeed, mergedOptions.pauseAutoPlayOnHover)

      this.waitForImages().then(() => {
        if (!this.flickity) return
        this.flickity.resize()
      })
    }

    this.flickity.on('ready', () => {
      requestAnimationFrame(() => {
        const viewport = this.querySelector('.flickity-viewport')
        if (viewport && viewport.style.height) {
          requestAnimationFrame(() => {
            if (this.style.minHeight) {
              this.style.minHeight = ''
            }
          })
        }
      })
    })
  }

  waitForImages() {
    const imgs = this.querySelectorAll('img')
    return Promise.all(
      Array.from(imgs).map((img) => {
        if (img.complete) return Promise.resolve()
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true })
          img.addEventListener('error', resolve, { once: true })
        })
      })
    )
  }

  fillTickerContent() {
    const viewportWidth = this.getBoundingClientRect().width
    const cells = Array.from(this.children).filter((el) => el.tagName !== 'STYLE')
    if (!cells.length) return

    let totalWidth = 0
    cells.forEach((cell) => {
      const rect = cell.getBoundingClientRect()
      const style = getComputedStyle(cell)
      totalWidth += rect.width + (parseFloat(style.marginRight) || 0)
    })

    if (totalWidth >= viewportWidth * 2) return

    const copies = Math.ceil((viewportWidth * 2) / totalWidth) - 1
    for (let i = 0; i < copies; i++) {
      cells.forEach((cell) => {
        const clone = cell.cloneNode(true)
        clone.setAttribute('data-ticker-clone', '')
        clone.removeAttribute('id')
        this.appendChild(clone)
      })
    }
  }

  eagerLoadAllImages() {
    this.querySelectorAll('img[data-src], img[data-srcset]').forEach((img) => {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset
        delete img.dataset.srcset
      }
      if (img.dataset.sizes) {
        img.sizes = img.dataset.sizes
        delete img.dataset.sizes
      }
      if (img.dataset.src) {
        img.src = img.dataset.src
        delete img.dataset.src
      }
      img.removeAttribute('loading')
      img.classList.remove('lazy')
    })
  }

  setupTicker(speed, pauseOnHover) {
    this.tickerSpeed = speed
    this.tickerPaused = false
    this.tickerRAF = null

    this.flickity.x = 0

    this._originalPositionSlider = this.flickity.positionSlider
    this.flickity.positionSlider = () => {
      const f = this.flickity
      if (!f.cells.length) return

      const offset = (((-f.x) % f.slideableWidth) + f.slideableWidth) % f.slideableWidth

      f.cells.forEach((cell) => {
        let pos = ((cell.x - offset) % f.slideableWidth + f.slideableWidth) % f.slideableWidth
        if (pos > f.size.innerWidth + cell.size.outerWidth) pos -= f.slideableWidth
        f._renderCellPosition(cell, pos)
      })

      f.slider.style.transform = 'translateX(0)'
      f.dispatchScrollEvent()
    }

    this._originalApplySelectedAttraction = this.flickity.applySelectedAttraction
    this.flickity.applySelectedAttraction = () => {}

    this._originalNext = this.flickity.next
    this._originalPrevious = this.flickity.previous
    const slideStep = this.flickity.slideableWidth / this.flickity.slides.length

    this.flickity.next = () => {
      this.flickity.x -= slideStep
      this.flickity.positionSlider()
    }

    this.flickity.previous = () => {
      this.flickity.x += slideStep
      this.flickity.positionSlider()
    }

    if (pauseOnHover) {
      const onPointerDown = () => { this.tickerPaused = true }
      const onPointerUp = () => { this.tickerPaused = false }
      this.flickity.on('pointerDown', onPointerDown)
      this.flickity.on('pointerUp', onPointerUp)

      this._onTickerMouseEnter = () => { this.tickerPaused = true }
      this._onTickerMouseLeave = () => { this.tickerPaused = false }
      this.addEventListener('mouseenter', this._onTickerMouseEnter)
      this.addEventListener('mouseleave', this._onTickerMouseLeave)

      this._pauseCleanup = () => {
        this.flickity.off('pointerDown', onPointerDown)
        this.flickity.off('pointerUp', onPointerUp)
        this.removeEventListener('mouseenter', this._onTickerMouseEnter)
        this.removeEventListener('mouseleave', this._onTickerMouseLeave)
        this._onTickerMouseEnter = null
        this._onTickerMouseLeave = null
      }
    }

    this.tickerCleanup = () => {
      if (this.flickity) {
        if (this._originalPositionSlider) {
          this.flickity.positionSlider = this._originalPositionSlider
        }
        if (this._originalApplySelectedAttraction) {
          this.flickity.applySelectedAttraction = this._originalApplySelectedAttraction
        }
        if (this._originalNext) {
          this.flickity.next = this._originalNext
          this.flickity.previous = this._originalPrevious
        }
      }
      this._originalPositionSlider = null
      this._originalApplySelectedAttraction = null
      this._originalNext = null
      this._originalPrevious = null
      if (this._pauseCleanup) {
        this._pauseCleanup()
        this._pauseCleanup = null
      }
    }

    this.flickity.positionSlider()

    const tick = () => {
      if (!this.tickerPaused && this.flickity && !this.flickity.isAnimating) {
        this.flickity.x -= this.tickerSpeed
        this.flickity.positionSlider()
      }
      this.tickerRAF = requestAnimationFrame(tick)
    }

    this.tickerRAF = requestAnimationFrame(tick)
  }

  destroyTicker() {
    if (this.tickerRAF) {
      cancelAnimationFrame(this.tickerRAF)
      this.tickerRAF = null
    }
    if (this.tickerCleanup) {
      this.tickerCleanup()
      this.tickerCleanup = null
    }
  }

  isFlickityElement(element) {
    if (!element.classList) return false

    return Array.from(element.classList).some((className) => className.startsWith('flickity-'))
  }

  setupMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldReinitialize = false

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const isRelevantNode = (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            node.tagName !== 'STYLE' &&
            !this.isFlickityElement(node) &&
            !node.hasAttribute('data-ticker-clone')
          const hasRelevantChanges =
            Array.from(mutation.addedNodes).some(isRelevantNode) ||
            Array.from(mutation.removedNodes).some(isRelevantNode)

          if (hasRelevantChanges) {
            shouldReinitialize = true
          }
        }
      })

      if (shouldReinitialize) {
        this.reinitializeCarousel()
      }
    })

    this.mutationObserver.observe(this, {
      childList: true,
      subtree: false
    })
  }

  removeTickerClones() {
    this.querySelectorAll('[data-ticker-clone]').forEach((el) => el.remove())
  }

  reinitializeCarousel() {
    this.destroyTicker()

    if (this.flickity) {
      this.flickity.destroy()
      this.flickity = null
    }

    this.removeTickerClones()

    requestAnimationFrame(() => {
      this.initializeCarousel()
    })
  }

  observedAttributes = ['carousel-settings']

  get carouselSettings() {
    const carouselSettings = this.getAttribute('carousel-settings')
    if (!carouselSettings) return {}
    const parsedSettings = parseJSON(carouselSettings)
    if (parsedSettings?.groupCells !== undefined) {
      const value = parsedSettings.groupCells

      if (value === 'true') {
        parsedSettings.groupCells = true
      } else if (value === 'false') {
        parsedSettings.groupCells = false
      } else if (!isNaN(value) && typeof value === 'string') {
        parsedSettings.groupCells = Number(value)
      }
    }
    return parsedSettings
  }

  disconnectedCallback() {
    this.destroyTicker()
    this.removeTickerClones()

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    if (this.flickity) {
      this.flickity.destroy()
      this.flickity = null
    }
  }
}
